// Scene 0 - Space waehlen.
// Portiert aus Mock: SPACES-Grid, Auswahl mit Glow + Check-Pop, Details-
// Unfold mit selbstzeichnender Pipeline-SVG und Count-Ups fuer Pakete/Artefakte.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "../../../components/Skeleton";
import { useLang } from "../i18n";
import { useTransportV2 } from "../context/TransportV2Context";
import { useSpaceLoader } from "../hooks/useSpaceLoader";
import { usePipelineHealth } from "../hooks/usePipelineHealth";
import PipelineSvg from "../components/PipelineSvg";
import { Cx } from "../components/IconSprite";

export default function Scene0Space() {
  const { lang, t } = useLang();
  const { state, dispatch } = useTransportV2();
  const { spaces, loading, error } = useSpaceLoader(lang);
  // Die alte Kachel "geplante Transporte" bezog ihre Zahl aus /api/transport-requests
  // (Vier-Augen-Schleuse). Der Endpunkt existiert seit 2026-08-17 nicht mehr. Statt
  // eine erfundene Null anzuzeigen, entfaellt die Kachel vollstaendig - Scene4/Scene5
  // und der Context (scheduleCount) bleiben unberuehrt.
  const [showDetails, setShowDetails] = useState(false);
  const unfoldRef = useRef(null);

  const selected = useMemo(() => spaces.find((s) => s.key === state.spaceKey) || null, [spaces, state.spaceKey]);

  // Details-Unfold anschmeissen, wenn ausgewaehlt.
  useEffect(() => {
    setShowDetails(!!selected);
  }, [selected]);

  const pick = useCallback((sp) => {
    if (!sp.usable) return;
    dispatch({
      type: "PICK_SPACE",
      payload: {
        space: { id: sp.id, name: sp.name, key: sp.key, stages: sp.stages, pakete: sp.pakete, artefakte: sp.artefakte, stufen: sp.stufen, quellStufe: sp.quellStufe, zielStufe: sp.zielStufe },
        spaceKey: sp.key,
        sourceInstance: sp.sourceInstance,
        qasInstance: sp.qasInstance,
        prodInstance: sp.prodInstance,
      },
    });
  }, [dispatch]);

  const next = useCallback(() => dispatch({ type: "SET_STEP", payload: 1 }), [dispatch]);

  // Pipeline-Einrichtungsstatus fuer die Bereitschaft-Spalte. Erst laden, wenn
  // ein Space wirklich ausgewaehlt ist (der Endpoint macht GitHub-Aufrufe).
  const health = usePipelineHealth(selected?.id || null);

  const pipelineStages = useMemo(() => {
    if (!selected) return [];
    return selected.stages.map((st) => ({ code: st.code, name: st.name, glyph: st.glyph }));
  }, [selected]);
  // Zielindex aus der wirklich gewaehlten Zielstufe. "vorletzte Stufe" traf nur zufaellig bei
  // DEV/QAS/PROD zu - bei vier Stufen zeigte die Grafik PRE-PROD, transportiert wurde nach QAS.
  const tgtIdx = selected
    ? Math.max(0, selected.stages.findIndex((st) => st.stageKey === selected.zielStufe))
    : 0;

  return (
    <section className="scene active scene-enter" id="s0">
      <div className="sh">
        <h2>{t("s0.h2")}</h2>
        <p dangerouslySetInnerHTML={{ __html: t("s0.p") }} />
      </div>

      <div className={"space-cards-grid" + (state.spaceKey ? " has-selection" : "")}>
        {loading && <Skeleton variant="cards" count={2} columns={2} className="iio-skel--span" label={t("common.loading")} />}
        {error && (
          <div className="errline">
            <span style={{ flex: 1 }}>{t("err.instances")}: {error}</span>
          </div>
        )}
        {!loading && !error && spaces.map((sp) => {
          const path = sp.stages.map((st) => st.code);
          const isSelected = state.spaceKey === sp.key;
          const pathFlow = (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {path.map((code, i) => (
                <React.Fragment key={code + i}>{i > 0 && <Cx />}<span>{code}</span></React.Fragment>
              ))}
            </span>
          );
          const desc = sp.usable
            ? (sp.werkeList?.length
                ? t("s0.chainW", { path: pathFlow, n: sp.werkeList.length })
                : t("s0.chain", { path: pathFlow }))
            : (sp.reason ? t(sp.reason) : "");
          return (
            <button
              key={sp.key}
              type="button"
              className={"space-card" + (isSelected ? " selected" : "")}
              onClick={() => pick(sp)}
              disabled={!sp.usable}
              style={!sp.usable ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
            >
              <span className="sc-top">
                <span className="sc-ic"><svg className="ic ic-20"><use href="#ic-spaces" /></svg></span>
                <span className="sc-name">{sp.name}</span>
                <span className="card-check">
                  <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path className="check-path" d="M5 12.5 L10 17.5 L19 6.5" />
                  </svg>
                </span>
              </span>
              <span className="sc-desc">{desc}</span>
              <span className="sc-div" />
              <span className="sc-kv">
                <span className="k">{t("s0.status")}</span>
                <span className="v st st--ok"><i className="dot" />{t("s0.active")}</span>
              </span>
              <span className="sc-kv">
                <span className="k">{t("s0.sys")}</span>
                <span className="v">{sp.stages.length}</span>
              </span>
              <span className="sc-kv">
                <span className="k">{t("s0.path")}</span>
                <span className="v">
                  {path.map((code, i) => (
                    <React.Fragment key={code + i}>
                      {i > 0 && <Cx />}
                      <span>{code}</span>
                    </React.Fragment>
                  ))}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className={"unfold" + (showDetails ? " open" : "")} ref={unfoldRef}>
        <div className="unfold-inner">
          {selected && (
            <div className="panel space-details">
              <div className="sd-title">
                <svg className="ic" style={{ color: "var(--signal)" }}><use href="#ic-spaces" /></svg>
                {t("s0.details", { name: selected.name })}
              </div>
              <div className="sd-viz">
                <PipelineSvg stages={pipelineStages} tgtIdx={tgtIdx} />
              </div>
              <div className="sd-cols">
                <div className="detail-col" style={{ "--i": 0 }}>
                  <div className="dc-h"><svg className="ic ic-16"><use href="#ic-spaces" /></svg>{t("col.space")}</div>
                  <div className="dc-b">
                    <b>{selected.name}</b>
                    <span className="tiny">ID: {selected.id || "-"}</span>
                    <span className="tiny">
                      {selected.werkeList?.length
                        ? t("s0.werkeIn", { n: selected.werkeList.length, list: selected.werkeList.map((w) => w.kennung).join(", ") })
                        : t("s0.noW")}
                    </span>
                  </div>
                </div>
                <div className="detail-col" style={{ "--i": 1 }}>
                  <div className="dc-h">
                    <svg className="ic ic-16"><use href="#ic-instances" /></svg>
                    {t("col.land")} <span className="muted" style={{ fontWeight: 500 }}>{t("col.landsub")}</span>
                  </div>
                  <div className="dc-b">
                    <span className="dc-land">
                      {selected.stages.map((st, i) => (
                        <React.Fragment key={st.code + i}>
                          <span className="stg"><b>{st.code}</b><span>{st.name}</span></span>
                          {i < selected.stages.length - 1 && <Cx />}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                </div>
                <div className="detail-col" style={{ "--i": 2 }}>
                  <div className="dc-h"><svg className="ic ic-16"><use href="#ic-pipeline" /></svg>{t("col.path")}</div>
                  <div className="dc-b">
                    <span style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                      {selected.stages.map((st, i) => (
                        <React.Fragment key={"p" + st.code + i}>
                          {i > 0 && <Cx />}
                          <span>{st.code}</span>
                        </React.Fragment>
                      ))}
                    </span>
                    <span className="tiny">{t("s0.pathsub", { code: selected.stages[Math.max(0, selected.stages.length - 2)].code })}</span>
                  </div>
                </div>
                <div className="detail-col" style={{ "--i": 3 }}>
                  <div className="dc-h"><svg className="ic ic-16"><use href="#ic-audit" /></svg>{t("col.ready")}</div>
                  <div className="dc-b">
                    {/* Bereitschaft = Zustand der CI/CD-Pipeline dieses Space. Frueher standen hier
                        Paket-/Artefakt-Zahlen, die auf der Kachel keine Rolle spielen. Ein Space ohne
                        eingerichtete Pipeline kann nicht transportieren - genau das gehoert her. */}
                    {health.loading && (
                      <span className="tiny muted">{t("s0.readyPending")}</span>
                    )}
                    {!health.loading && health.forbidden && (
                      <span className="tiny muted">{t("s0.readyUnknown")}</span>
                    )}
                    {!health.loading && !health.forbidden && (
                      <>
                        <span className={"st " + (health.configured ? "st--ok" : "st--warn")} style={{ fontSize: 12.5 }}>
                          <i className="dot" />{health.configured ? t("s0.readyRepo") : t("s0.readyRepoNo")}
                        </span>
                        <span className={"st " + (health.workflowActive ? "st--ok" : "st--warn")} style={{ fontSize: 12.5 }}>
                          <i className="dot" />{health.workflowActive ? t("s0.readyWf") : t("s0.readyWfNo")}
                        </span>
                        <span className={"st " + (health.stagesReady ? "st--ok" : "st--warn")} style={{ fontSize: 12.5 }}>
                          <i className="dot" />{health.stagesReady ? t("s0.readyStages") : t("s0.readyStagesNo")}
                        </span>
                        <span
                          className={"st " + (health.ready ? "st--ok" : "st--warn")}
                          style={{ fontSize: 12.5, marginTop: 4, fontWeight: 700 }}
                        >
                          <i className="dot" />{health.ready ? t("s0.ready") : t("s0.readyOpen")}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="sd-info">
                <svg className="ic ic-16"><use href="#ic-info" /></svg>
                <span>{t("s0.info")}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="footbar">
        <span />
        <button className="btn btn-cta" onClick={next} disabled={!state.spaceKey}>
          {t("s0.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>
    </section>
  );
}
